import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CoachLayout } from '../components/CoachLayout';
import { venuesAPI, getErrorMessage } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const ITEMS_PER_PAGE = 10;

export default function VenuesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const emptyFormData = useMemo(() => ({
    name: '',
    fixed_cost_per_hour: '',
    variable_costs: [],
  }), []);
  const [formData, setFormData] = useState(emptyFormData);

  useEffect(() => {
    fetchVenues();
  }, []);

  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setEditingVenue(null);
      setFormData(emptyFormData);
      setDialogOpen(true);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('create');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams, emptyFormData]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;
    if (venues.length === 0) return;
    const target = venues.find((venue) => venue.id === editId);
    if (target) {
      setEditingVenue(target);
      setFormData({
        name: target.name,
        fixed_cost_per_hour: target.fixed_cost_per_hour.toString(),
        variable_costs: target.variable_costs || [],
      });
      setDialogOpen(true);
    } else {
      toast.error('Struttura non trovata');
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, venues]);

  const fetchVenues = async () => {
    try {
      const response = await venuesAPI.getAll();
      // Ordina strutture in ordine alfabetico per nome
      const sortedVenues = response.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })
      );
      setVenues(sortedVenues);
    } catch (error) {
      toast.error('Errore nel caricamento delle strutture');
    } finally {
      setLoading(false);
    }
  };

  // Paginazione
  const totalPages = Math.ceil(venues.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedVenues = venues.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        fixed_cost_per_hour: parseFloat(formData.fixed_cost_per_hour),
      };

      if (editingVenue) {
        await venuesAPI.update(editingVenue.id, data);
        toast.success('Struttura aggiornata!');
      } else {
        await venuesAPI.create(data);
        toast.success('Struttura creata!');
        if (sessionStorage.getItem('onboarding_active') === 'true') {
          window.dispatchEvent(new CustomEvent('onboarding:advance', { detail: { step: 2 } }));
        }
      }
      setDialogOpen(false);
      setFormData(emptyFormData);
      setEditingVenue(null);
      fetchVenues();
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    }
  };

  const handleEdit = (venue) => {
    setEditingVenue(venue);
    setFormData({
      name: venue.name,
      fixed_cost_per_hour: venue.fixed_cost_per_hour.toString(),
      variable_costs: venue.variable_costs || [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa struttura?')) return;

    try {
      await venuesAPI.delete(id);
      toast.success('Struttura eliminata');
      fetchVenues();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore durante l\'eliminazione'));
    }
  };

  const addVariableCost = () => {
    setFormData({
      ...formData,
      variable_costs: [...formData.variable_costs, { min_athletes: '', max_athletes: '', cost_per_hour: '' }],
    });
  };

  const removeVariableCost = (index) => {
    const newCosts = formData.variable_costs.filter((_, i) => i !== index);
    setFormData({ ...formData, variable_costs: newCosts });
  };

  const updateVariableCost = (index, field, value) => {
    const newCosts = [...formData.variable_costs];
    newCosts[index][field] = field === 'cost_per_hour' ? parseFloat(value) || 0 : parseInt(value) || 0;
    setFormData({ ...formData, variable_costs: newCosts });
  };

  if (loading) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-[#ccff00] text-xl font-bold">Caricamento...</div>
        </div>
      </CoachLayout>
    );
  }

  return (
    <CoachLayout>
      <div data-testid="venues-page" className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-1 pb-4 sm:pt-2 sm:pb-8 md:pt-6">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl font-black text-white mb-1 sm:mb-2">Strutture</h1>
            <p className="text-zinc-400 text-sm sm:text-base">Gestisci le strutture sportive ({venues.length} totali)</p>
          </div>

          <Button
            onClick={() => {
              setEditingVenue(null);
              setFormData(emptyFormData);
              setDialogOpen(true);
            }}
            className="hidden md:flex bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl h-10"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuova Struttura
          </Button>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent 
            onInteractOutside={(e) => e.preventDefault()}
  onPointerDownOutside={(e) => e.preventDefault()}
  className="bg-zinc-900 border border-white/10 rounded-xl max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingVenue ? 'Modifica Struttura' : 'Nuova Struttura'}
                </DialogTitle>
                <DialogDescription className="text-zinc-400">
                  {editingVenue ? 'Modifica i dettagli della struttura' : 'Aggiungi una nuova struttura sportiva'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-zinc-300">Nome Struttura</Label>
                  <Input
                    id="name"
                    data-testid="venue-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-[#ccff00] rounded-xl"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fixed_cost" className="text-zinc-300">Costo Fisso Orario (€)</Label>
                  <Input
                    id="fixed_cost"
                    data-testid="venue-fixed-cost-input"
                    type="number"
                    step="0.01"
                    value={formData.fixed_cost_per_hour}
                    onChange={(e) => setFormData({ ...formData, fixed_cost_per_hour: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-[#ccff00] rounded-xl"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-zinc-300">Tariffe variabili in base al numero di atleti in compresenza</Label>
                    <Button
                      type="button"
                      onClick={addVariableCost}
                      variant="outline"
                      size="sm"
                      className="text-[#ccff00] border-[#ccff00]/30 rounded-xl"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Aggiungi
                    </Button>
                  </div>
                  {formData.variable_costs.map((cost, index) => (
                    <div key={index} className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label className="text-xs text-zinc-400">Min Atleti</Label>
                        <Input
                          type="number"
                          value={cost.min_athletes}
                          onChange={(e) => updateVariableCost(index, 'min_athletes', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 h-10 rounded-xl"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-zinc-400">Max Atleti</Label>
                        <Input
                          type="number"
                          value={cost.max_athletes}
                          onChange={(e) => updateVariableCost(index, 'max_athletes', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 h-10 rounded-xl"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-zinc-400">Costo/ora (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={cost.cost_per_hour}
                          onChange={(e) => updateVariableCost(index, 'cost_per_hour', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 h-10 rounded-xl"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={() => removeVariableCost(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <Button
                  data-testid="save-venue-btn"
                  type="submit"
                  className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl"
                >
                  {editingVenue ? 'Aggiorna' : 'Crea'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {venues.length === 0 ? (
          <div className="glass-card p-12 rounded-xl border border-white/10 text-center">
            <p className="text-zinc-400 text-lg">Nessuna struttura trovata. Aggiungi la tua prima struttura!</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {paginatedVenues.map((venue) => (
                <div
                  key={venue.id}
                  data-testid="venue-card"
                  className="glass-card p-3 rounded-xl border border-white/10 hover:border-[#ccff00]/30 transition-colors group cursor-pointer"
                  onClick={() => navigate(`/venues/${venue.id}`)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-8 h-8 text-[#ccff00]" />
                      <h3 className="text-lg sm:text-xl font-bold text-white group-hover:text-[#ccff00]">{venue.name}</h3>
                    </div>
                    <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        data-testid="edit-venue-btn"
                        onClick={() => handleEdit(venue)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-[#ccff00]"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid="delete-venue-btn"
                        onClick={() => handleDelete(venue.id)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400 text-sm">Costo Fisso:</span>
                      <span className="text-white font-mono font-bold">€{venue.fixed_cost_per_hour}/h</span>
                    </div>

                    {venue.variable_costs && venue.variable_costs.length > 0 && (
                      <div className="mt-1.5 pt-1.5 border-t border-white/10">
                        <div className="text-xs text-zinc-400 mb-0.5">Costi Variabili:</div>
                        {venue.variable_costs.map((cost, idx) => (
                          <div key={idx} className="text-xs text-zinc-300 flex justify-between">
                            <span>{cost.min_athletes}-{cost.max_athletes} atleti:</span>
                            <span className="font-mono font-bold">€{cost.cost_per_hour}/h</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Paginazione */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-4 mt-8">
                <Button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Indietro
                </Button>
                <span className="text-zinc-400">
                  Pagina {currentPage} di {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl"
                >
                  Avanti
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </CoachLayout>
  );
}
